import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { NmButton } from './NmButton';

import { ConfirmDialogContext, type ConfirmOptions } from './confirmDialogContext';
export { useConfirm } from './confirmDialogContext';
export type { ConfirmOptions } from './confirmDialogContext';

type ConfirmRequest = ConfirmOptions & {
  resolve: (confirmed: boolean) => void;
};


function normalizeOptions(input: ConfirmOptions | string): ConfirmOptions {
  if (typeof input === 'string') {
    return { message: input, variant: 'danger' };
  }
  return input;
}

function DangerIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function ConfirmDialogView({
  request,
  onConfirm,
  onCancel,
}: {
  request: ConfirmRequest;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const isDanger = request.variant !== 'default';
  const title = request.title ?? (isDanger ? t('confirm.titleDelete') : t('confirm.title'));
  const confirmLabel = request.confirmLabel ?? (isDanger ? t('confirm.delete') : t('confirm.confirm'));
  const cancelLabel = request.cancelLabel ?? t('confirm.cancel');

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onCancel]);

  return createPortal(
    <div
      className="nm-confirm-overlay animate-fadein"
      role="presentation"
      onMouseDown={e => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div
        className="nm-confirm-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="nm-confirm-title"
        aria-describedby="nm-confirm-message"
      >
        <div className={`nm-confirm-dialog__icon ${isDanger ? 'nm-confirm-dialog__icon--danger' : ''}`}>
          {isDanger ? <DangerIcon /> : null}
        </div>

        <h2 id="nm-confirm-title" className="nm-confirm-dialog__title">
          {title}
        </h2>

        <p id="nm-confirm-message" className="nm-confirm-dialog__message">
          {request.message}
        </p>

        <div className="nm-confirm-dialog__actions">
          <NmButton variant="neutral" size="sm" autoFocus onClick={onCancel}>
            {cancelLabel}
          </NmButton>
          <NmButton
            variant={isDanger ? 'danger' : 'primary'}
            size="sm"
            onClick={onConfirm}
          >
            {confirmLabel}
          </NmButton>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Global provider — replaces native `window.confirm` with neumorphic dialogs. */
export function ConfirmDialogProvider({ children }: { children: React.ReactNode }) {
  const [request, setRequest] = useState<ConfirmRequest | null>(null);

  const confirm = useCallback((input: ConfirmOptions | string): Promise<boolean> => {
    const options = normalizeOptions(input);
    return new Promise<boolean>(resolve => {
      setRequest({ ...options, resolve });
    });
  }, []);

  const close = useCallback((confirmed: boolean) => {
    setRequest(prev => {
      prev?.resolve(confirmed);
      return null;
    });
  }, []);

  return (
    <ConfirmDialogContext.Provider value={{ confirm }}>
      {children}
      {request && (
        <ConfirmDialogView
          request={request}
          onConfirm={() => close(true)}
          onCancel={() => close(false)}
        />
      )}
    </ConfirmDialogContext.Provider>
  );
}
